import {
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { supportedHorizonLabel } from './portfolioRisk'
import type { PortfolioHazardAal, summarizeCombinedAal } from './portfolioAal'

type Props = { hazards: PortfolioHazardAal[]; summary: ReturnType<typeof summarizeCombinedAal>; currency: string }

export default function PortfolioAalMetadata({ hazards, summary, currency }: Props) {
  const money = new Intl.NumberFormat('en-GB', { style: 'currency', currency, maximumFractionDigits: 0 })
  const amount = (value: number | null) => (value == null ? '—' : money.format(value))
  return (
    <Stack spacing={1.5}>
      <Typography variant="body2">
        Combined average annual loss (AAL) adds modelled property damage and business-interruption (BI) losses for the
        selected scenario, horizon and adaptation view.
      </Typography>
      <Typography variant="body2">
        Hazard estimates are summed independently. Portfolio contributors count each property once.
      </Typography>
      <Typography variant="body2">
        Portfolio contributors:{' '}
        <strong>
          {summary.loading || summary.error ? '—' : `${summary.contributors} of ${summary.total} properties`}
        </strong>
      </Typography>
      <TableContainer>
        <Table size="small" aria-label="Combined AAL calculation and contributors">
          <TableHead>
            <TableRow>
              <TableCell>Hazard</TableCell>
              <TableCell>Damage AAL</TableCell>
              <TableCell>BI AAL</TableCell>
              <TableCell>Hazard AAL</TableCell>
              <TableCell>Properties</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {hazards.map(hazard => {
              const status = !hazard.modelled
                ? 'Risk analysis only'
                : !hazard.periodAvailable
                ? `${supportedHorizonLabel(hazard.hazard.name)} only`
                : hazard.loading
                ? 'Loading…'
                : hazard.error
                ? 'Reload results'
                : null
              return (
                <TableRow key={hazard.hazard.name}>
                  <TableCell>{hazard.hazard.display_name}</TableCell>
                  {status ? (
                    <TableCell colSpan={4}>
                      {hazard.error ? (
                        <Button size="small" onClick={hazard.retry}>
                          {status}
                        </Button>
                      ) : (
                        status
                      )}
                    </TableCell>
                  ) : (
                    <>
                      <TableCell>
                        <Typography variant="body2">{amount(hazard.summary.damage.value)}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {hazard.summary.damage.contributors} properties
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{amount(hazard.summary.bi.value)}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {hazard.summary.bi.contributors} properties
                        </Typography>
                      </TableCell>
                      <TableCell>{amount(hazard.summary.combined.value)}</TableCell>
                      <TableCell>
                        {hazard.summary.combined.contributors} / {hazard.summary.combined.total}
                      </TableCell>
                    </>
                  )}
                </TableRow>
              )
            })}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell sx={{ fontWeight: 800, color: 'text.primary' }}>Combined AAL</TableCell>
              <TableCell colSpan={2} />
              <TableCell sx={{ fontWeight: 800, color: 'text.primary' }}>{amount(summary.value)}</TableCell>
              <TableCell>
                {summary.loading || summary.error ? '—' : `${summary.contributors} / ${summary.total}`}
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
      <Typography variant="caption" color="text.secondary">
        All amounts are per year. Contributor counts include recorded zero losses. Adapted mode uses adapted results
        where available and raw results otherwise.
      </Typography>
    </Stack>
  )
}
